import { Link } from 'react-router-dom';
import { Eye, Heart, Music } from 'lucide-react';
import { cn } from '../../lib/cn.js';
import { formatNumber } from '../../lib/format.js';

interface SongCardProps {
  slug: string;
  title: string;
  artistName: string;
  artistSlug: string;
  coverUrl?: string | null;
  genreName?: string | null;
  viewCount?: number;
  likeCount?: number;
  variant?: 'vertical' | 'horizontal' | 'list';
  index?: number;
  className?: string;
}

export function SongCard({
  slug,
  title,
  artistName,
  artistSlug,
  coverUrl,
  genreName,
  viewCount = 0,
  likeCount = 0,
  variant = 'vertical',
  index,
  className,
}: SongCardProps) {
  if (variant === 'list') {
    return (
      <Link
        to={`/songs/${slug}`}
        className={cn(
          'flex items-center gap-4 px-3 py-2.5 rounded-lg hover:bg-surface-hover transition-colors group',
          className,
        )}
      >
        {index !== undefined && (
          <span className="w-6 text-right text-sm font-medium text-text-muted">{index + 1}</span>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-text-primary truncate group-hover:text-primary transition-colors">
            {title}
          </p>
          <p className="text-xs text-text-secondary truncate">{artistName}</p>
        </div>
        <div className="flex items-center gap-3 text-xs text-text-muted shrink-0">
          <span className="flex items-center gap-1">
            <Eye size={12} />
            {formatNumber(viewCount)}
          </span>
          <span className="flex items-center gap-1">
            <Heart size={12} />
            {formatNumber(likeCount)}
          </span>
        </div>
      </Link>
    );
  }

  if (variant === 'horizontal') {
    return (
      <Link
        to={`/songs/${slug}`}
        className={cn(
          'flex items-center gap-3 p-2 rounded-lg hover:bg-surface-hover transition-colors group',
          className,
        )}
      >
        <div className="w-14 h-14 rounded-lg bg-surface-hover overflow-hidden shrink-0">
          {coverUrl ? (
            <img src={coverUrl} alt={title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Music size={20} className="text-text-muted" />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-text-primary truncate group-hover:text-primary transition-colors">
            {title}
          </p>
          <div className="flex items-center gap-2 text-xs text-text-secondary">
            <Link
              to={`/artists/${artistSlug}`}
              className="hover:text-primary transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              {artistName}
            </Link>
            {genreName && (
              <>
                <span className="text-text-muted">·</span>
                <span>{genreName}</span>
              </>
            )}
            <span className="text-text-muted">·</span>
            <span>{formatNumber(viewCount)} vistas</span>
          </div>
        </div>
      </Link>
    );
  }

  // Vertical (default)
  return (
    <Link
      to={`/songs/${slug}`}
      className={cn(
        'group block rounded-xl overflow-hidden bg-surface hover:bg-surface-hover transition-all hover:shadow-lg hover:shadow-primary/5',
        className,
      )}
    >
      <div className="aspect-square relative overflow-hidden bg-surface-hover">
        {coverUrl ? (
          <img
            src={coverUrl}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Music size={40} className="text-text-muted" />
          </div>
        )}
      </div>
      <div className="p-3">
        <p className="text-sm font-semibold text-text-primary truncate group-hover:text-primary transition-colors">
          {title}
        </p>
        <p className="text-xs text-text-secondary truncate mt-0.5">{artistName}</p>
        <div className="flex items-center justify-between mt-2">
          {genreName && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
              {genreName}
            </span>
          )}
          <span className="text-[10px] text-text-muted flex items-center gap-1 ml-auto">
            <Eye size={10} />
            {formatNumber(viewCount)}
          </span>
        </div>
      </div>
    </Link>
  );
}
